import React, { useState } from 'react';
import { useFormik } from 'formik';
import { RequiredFieldMarker } from '../Competition/components/fields/requiredFieldMarker';
import { ValidationMessage } from '../Helpers/validation/validationMessage';
import { getLabel } from '../Language/labels';
import { get } from 'lodash';
import * as Yup from 'yup';
import { updateProfile } from './api/profile';
import { format } from 'date-fns';
import { Processing } from '../Competition/components/processing';
import { setCurrentPage } from '../Navigation/currentPage';
import { SCR_PROFILE_UPDATED } from '../enums';
import { setLoggedInUser } from '../LoginRegister/reducers/loginState';
import { recaptchaValidate } from '../LoginRegister/api/recaptchaValidate';
import { getErrorMessage } from '../Helpers/validation/apiErrorMessageHandler';

const getDate = (apiDate) => {
  const dateString = get(apiDate, 'date', '');

  if (dateString.length === 0) {
    return '';
  }

  return format(new Date(dateString), 'yyyy-MM-dd');
}

export const Profile = ({ dispatch, countries, nationalities, loggedInUser }) => {

  const [processing, setProcessing] = useState(false);
  const [apiError, setApiError] = useState('');

  const getInitialValues = () => {
    const profilesData = get(loggedInUser, 'profiles.data', []);

    if (profilesData.length > 0) {
      const { allow_marketing, allow_third_party } = loggedInUser;
      const { forename, surname, title, telephone, dob, country_id,
        nationality_id } = profilesData[0];
      return {
        title: title || '',
        forename: forename || '',
        surname: surname || '',
        telephone: telephone || '',
        dob: getDate(dob),
        country_id: country_id || '',
        nationality_id: nationality_id || '',
        allow_marketing: allow_marketing || '',
        allow_third_party: allow_third_party || '',
      }
    } else {
      return {
        title: '',
        forename: '',
        surname: '',
        telephone: '',
        dob: '',
        country_id: '',
        nationality_id: '',
        allow_marketing: '',
        allow_third_party: '',
      }
    }

  }

  const phoneRegExp = /(\+[0-9]{1,4})[0-9]*/;

  const profileSchema = Yup.object().shape({
    forename: Yup.string().required(getLabel('registerFirstNameRequired')),
    surname: Yup.string().required(getLabel('registerLastNameRequired')),
    country_id: Yup.number().required(getLabel('registerCountryRequired')),
    nationality_id: Yup.number().required(getLabel('registerNationalityRequired')),
    allow_marketing: Yup.number(),
    allow_third_party: Yup.number(),
    telephone: Yup.string().matches(phoneRegExp, getLabel('profileValidPhoneNumber')),
    dob: Yup.date(),
    title: Yup.string()
  });

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: profileSchema,
    onSubmit: async function (values, { setFieldError }) {
      setProcessing(true);
      //get the recaptcha token
      const recaptchaResult = await recaptchaValidate();
      const updateResult = await updateProfile(loggedInUser, values, recaptchaResult)
        .catch((error) => {
          console.log(error);
          setProcessing(false);
          const api_errors = get(error, 'response.data.errors', '');
          setApiError(getErrorMessage(api_errors));
        });

      if (updateResult) {
        window.setTimeout(() => {
          setProcessing(false);
          dispatch(setCurrentPage(SCR_PROFILE_UPDATED));

          const updatedUser = {
            ...loggedInUser,
            allow_marketing: get(updateResult, 'userUpdateResult.data.data.allow_marketing', ''),
            allow_third_party: get(updateResult, 'userUpdateResult.data.data.allow_third_party', ''),
            profiles: {
              data: [
                {
                  ...get(updateResult, 'profileUpdateResult.data.data', {})
                }
              ]
            }
          }

          dispatch(setLoggedInUser(updatedUser));

        }, 4000);
      }
    }
  })

  return (
    <div className='profile-form-container'>
      {processing && <Processing title={getLabel('profileUpdateProcessing')} />}
      <h1 className="overview-title">{getLabel('profileScreenTitle')}</h1>
      {apiError.length > 0 && <div className='text-2xl'>{apiError}</div>}
      <form className='form profile-form' onSubmit={formik.handleSubmit}>
        <label className='form-control profile-title inline-block' htmlFor="title">
          <span>{getLabel('profileTitle')}</span><RequiredFieldMarker placeholder={true} />
          <input type="text" id="title"
            onChange={formik.handleChange}
            value={formik.values.title} />
          <ValidationMessage error={formik.errors.title} touched={formik.touched.title} />
        </label>

        <div className='profile-form-inner grid grid-cols-4 gap-4'>
          <label className='form-control' htmlFor="forename">
            <span>{getLabel('registerFirstName')}</span><RequiredFieldMarker />
            <input type="text" id="forename" required
              onChange={formik.handleChange}
              value={formik.values.forename} />
            <ValidationMessage error={formik.errors.forename} touched={formik.touched.forename} />
          </label>

          <label className='form-control' htmlFor="surname">
            <span>{getLabel('registerLastName')}</span><RequiredFieldMarker />
            <input type="text" id="surname" required
              onChange={formik.handleChange}
              value={formik.values.surname} />
            <ValidationMessage error={formik.errors.surname} touched={formik.touched.surname} />
          </label>

          <label className='form-control' htmlFor="telephone">
            <span>{getLabel('profileTelephone')}</span><RequiredFieldMarker placeholder={true} />
            <input type="text" id="telephone"
              onChange={formik.handleChange}
              value={formik.values.telephone} />
            <ValidationMessage error={formik.errors.telephone} touched={formik.touched.telephone} />
          </label>

          <label className='form-control' htmlFor="dob">
            <span>{getLabel('profileDob')}</span><RequiredFieldMarker placeholder={true} />
            <input type="date" id="dob"
              onChange={formik.handleChange}
              value={formik.values.dob} />
            <ValidationMessage error={formik.errors.dob} touched={formik.touched.dob} />
          </label>

          <label className='form-control' htmlFor="country_id">
            <span>{getLabel('registerCountry')}</span><RequiredFieldMarker />
            <select id="country_id" onChange={formik.handleChange} value={formik.values.country_id} required>
              <option value="">{getLabel('registerSelectCountry')}</option>
              {countries.map(obj => <option key={obj.id} value={obj.id}>{obj.name}</option>)}
            </select>
            <ValidationMessage error={formik.errors.country_id} touched={formik.touched.country_id} />
          </label>

          <label className='form-control' htmlFor="nationality_id">
            <span>{getLabel('registerNationality')}</span><RequiredFieldMarker />
            <select id="nationality_id" onChange={formik.handleChange} value={formik.values.nationality_id} required>
              <option value="">{getLabel('registerSelectNationality')}</option>
              {nationalities.map(obj => <option key={obj.id} value={obj.id}>{obj.name}</option>)}
            </select>
            <ValidationMessage error={formik.errors.nationality_id} touched={formik.touched.nationality_id} />
          </label>

          <label className='form-control' htmlFor="allow_marketing">
            <span>{getLabel('registerNewsletter')}</span><RequiredFieldMarker placeholder={true} />
            <select id="allow_marketing" onChange={formik.handleChange} value={formik.values.allow_marketing}>
              <option value="">{getLabel('select')}</option>
              <option value="1">{getLabel('yes')}</option>
              <option value="0">{getLabel('no')}</option>
            </select>
            <ValidationMessage error={formik.errors.allow_marketing} touched={formik.touched.allow_marketing} />
          </label>

          <label className='form-control' htmlFor="allow_third_party">
            <span>{getLabel('registerThirdParty')}</span><RequiredFieldMarker placeholder={true} />
            <select id="allow_third_party" onChange={formik.handleChange} value={formik.values.allow_third_party}>
              <option value="">{getLabel('select')}</option>
              <option value="1">{getLabel('yes')}</option>
              <option value="0">{getLabel('no')}</option>
            </select>
            <ValidationMessage error={formik.errors.allow_third_party} touched={formik.touched.allow_third_party} />
          </label>
        </div>
        <div className='form-control register-form-submit'>
          <button className='form-submit' type="submit">{getLabel('saveProfileButton')}</button>
        </div>
      </form>
    </div>
  )
}